import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { getContentPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-10/utils';
import { getFormPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-28/utils';
import { getEmailPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-29/utils';
import { getSmsPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-124/utils';
import { getWorkflowPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-44/utils';
import { getSocialBroadcastPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-71/utils';
import { getCtaPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-42/utils';
import { getWebInteractivePerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-91/utils';
import { getWhatsAppPerformanceStats } from 'framework-asset-preview-lib/internal/objectTypes/0-830/utils/getWhatsAppPerformanceStats';
import cachedHttp from 'framework-asset-preview-lib/internal/shared/api/cachedHttp';
import I18n from 'I18n';
import getFasLabel from './utils/getFasLabel';

/**
 * Generic fetcher for performance metrics using campaigns/associations/assets/v3/mappings/single endpoint
 */
async function getGenericPerformanceStats({
  crmObject,
  campaignObjectId,
  typeDefinition
}) {
  if (!campaignObjectId) {
    return Promise.resolve([]);
  }
  try {
    var _fasResponse$typeDefi;
    const [metricsResponse, fasResponse] = await Promise.all([cachedHttp.post(`campaigns/associations/assets/v3/mappings/single`, {
      data: {
        startDateInt: 20060101,
        endDateInt: parseInt(I18n.moment().format('YYYY-MM-DD').replaceAll('-', ''), 10),
        campaignObjectId,
        assetObjectId: crmObject.objectId,
        objectType: crmObject.objectTypeId
      }
    }), cachedHttp.post('/framework-builder/v1/read/metadata/type/all/batch', {
      data: ['campaignsAssociations']
    })]);
    if (!metricsResponse || !metricsResponse.metrics || !typeDefinition) {
      return Promise.resolve([]);
    }
    const fas = (_fasResponse$typeDefi = fasResponse[typeDefinition.name]) === null || _fasResponse$typeDefi === void 0 ? void 0 : _fasResponse$typeDefi.campaignsAssociations.metadata;
    const perfGroup = [];
    Object.keys(metricsResponse.metrics).forEach(metric => {
      const isCrmMetric = fas && fas.crmPropertyCounters;
      if (isCrmMetric) {
        // If it is a CRM prop then we can use the data from the type definition
        perfGroup.push({
          value: metricsResponse.metrics[metric],
          propertyName: metric
        });
      } else {
        const label = getFasLabel({
          fas,
          metric,
          objectTypeId: typeDefinition.objectTypeId
        });
        if (label) {
          perfGroup.push({
            value: metricsResponse.metrics[metric],
            propertyName: metric,
            label,
            formatStyle: 'decimal'
          });
        }
      }
    });
    return [{
      stats: perfGroup
    }];
  } catch (error) {
    console.error('Error fetching generic performance stats:', error);
    return Promise.resolve([]);
  }
}

/**
 * Aggregator to fetch all stats for a given object type.
 * As we can't control where the information is coming from,
 * we can use separate endpoints for each object type and
 * always return the same payload.
 *
 * This way, the UI doesn't have to know where the information is coming from
 * and will always render the same consistent information.
 */
export function fetchStatisticsForObjectType({
  crmObject,
  nonCrmObject,
  options,
  campaignObjectId,
  typeDefinition
}) {
  switch (crmObject.objectTypeId) {
    case OBJECT_TYPE.FORM:
      return getFormPerformanceStats({
        crmObject,
        nonCrmObject
      });
    case OBJECT_TYPE.MARKETING_EMAIL:
      return getEmailPerformanceStats({
        crmObject,
        nonCrmObject,
        options
      });
    case OBJECT_TYPE.BLOG_POST:
    case OBJECT_TYPE.CONTENT:
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      return getContentPerformanceStats(crmObject);
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return getSocialBroadcastPerformanceStats({
        crmObject,
        nonCrmObject
      });
    case OBJECT_TYPE.WORKFLOW:
      return getWorkflowPerformanceStats({
        crmObject,
        nonCrmObject
      });
    case OBJECT_TYPE.CTA:
      return getCtaPerformanceStats(crmObject);
    case OBJECT_TYPE.MARKETING_SMS:
      return getSmsPerformanceStats(crmObject);
    case OBJECT_TYPE.WEB_INTERACTIVE:
      return getWebInteractivePerformanceStats(crmObject);
    case OBJECT_TYPE.WHATSAPP:
      return getWhatsAppPerformanceStats(crmObject);
    default:
      return getGenericPerformanceStats({
        crmObject,
        campaignObjectId,
        typeDefinition
      });
  }
}